import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  Send, Eye, MousePointerClick, TrendingUp, TrendingDown, ArrowRight,
  Download, Shield, AlertTriangle, CheckCircle2, Activity, Link2, Clock,
  BarChart3, Radio, Zap
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── Custom chart tooltip ──────────────────────────────────────────
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-white/10 bg-[hsl(var(--popover))] px-3 py-2 text-xs shadow-lg">
      <p className="font-medium text-foreground">{label}</p>
      <p className="text-muted-foreground">Campaigns: <span className="text-foreground font-mono">{payload[0].value}</span></p>
    </div>
  );
}

// ─── Status badge for events ───────────────────────────────────────
function EventStatusBadge({ status }) {
  const configs = {
    active: { label: 'Active', color: 'hsl(var(--status-success))', bg: 'hsl(var(--status-success) / 0.12)' },
    warning: { label: 'Warning', color: 'hsl(var(--status-warning))', bg: 'hsl(var(--status-warning) / 0.12)' },
    secure: { label: 'Secure', color: 'hsl(160 84% 39%)', bg: 'hsl(160 84% 39% / 0.12)' },
    error: { label: 'Error', color: 'hsl(var(--destructive))', bg: 'hsl(var(--destructive) / 0.12)' },
    info: { label: 'Info', color: 'hsl(var(--status-info))', bg: 'hsl(var(--status-info) / 0.12)' },
  };
  const c = configs[status] || configs.info;
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider" style={{ color: c.color, backgroundColor: c.bg }} data-testid={`event-status-${status}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color }} />
      {c.label}
    </span>
  );
}

// ─── KPI Card ──────────────────────────────────────────────────────
function KpiCard({ icon: Icon, label, value, delta, deltaType, iconColor }) {
  const isUp = deltaType === 'up';
  return (
    <div
      className="rounded-2xl border border-white/10 bg-[hsl(var(--pane))] p-5 flex flex-col gap-3 card-hover-lift"
      style={{ boxShadow: 'var(--shadow-elev-1)' }}
      data-testid={`kpi-card-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
          <Icon className="w-5 h-5" style={{ color: iconColor || 'hsl(var(--accent-warm))' }} />
        </div>
        {delta && (
          <div className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${
            isUp ? 'bg-[hsl(var(--status-success)/0.12)] text-[hsl(var(--status-success))]' : 'bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]'
          }`}>
            {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {delta}
          </div>
        )}
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground mb-1">{label}</p>
        <p className="text-3xl font-semibold text-foreground" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{value}</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ─── ANALYTICS PAGE ───────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════
export default function Analytics() {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartRange, setChartRange] = useState('week');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [summaryRes, eventsRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/summary`).then(r => r.json()),
        fetch(`${API_URL}/api/analytics/events?limit=10`).then(r => r.json()),
      ]);
      setSummary(summaryRes);
      setEvents(eventsRes.events || eventsRes || []);
    } catch (err) {
      console.error('Failed to load analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  // ─── Derived data ───
  const totalSent = summary?.total_sent || 0;
  const totalOpens = summary?.total_opens || 0;
  const totalClicks = summary?.total_clicks || 0;
  const openRate = totalSent > 0 ? ((totalOpens / totalSent) * 100).toFixed(1) : '0.0';

  // Weekly chart data — real data from API, or empty
  const weeklyData = useMemo(() => {
    if (summary?.weekly_data) return summary.weekly_data;
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(d => ({ name: d, value: 0 }));
  }, [summary]);

  const monthlyData = useMemo(() => {
    if (summary?.monthly_data) return summary.monthly_data;
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map(m => ({ name: m, value: 0 }));
  }, [summary]);

  const chartData = chartRange === 'week' ? weeklyData : monthlyData;
  const hasChartData = chartData.some(d => d.value > 0);

  // Top clicked links — real data only, no mock fallback
  const topLinks = useMemo(() => {
    if (summary?.top_links?.length) return summary.top_links;
    return [];
  }, [summary]);

  // Recent events — real data only, no mock fallback
  const recentEvents = useMemo(() => {
    if (events.length > 0) return events.slice(0, 8);
    return [];
  }, [events]);

  // Export handler
  const handleExport = async (type) => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/export/${type}`);
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export.csv`;
      a.click();
      toast.success(`${type} data exported`);
    } catch (err) {
      toast.error(`Failed to export ${type}`);
    }
  };

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-8" data-testid="analytics-loading">
        {/* Hero skeleton */}
        <div className="space-y-3">
          <div className="h-3 w-32 rounded bg-white/5 animate-pulse" />
          <div className="h-10 w-80 rounded-lg bg-white/5 animate-pulse" />
          <div className="h-4 w-64 rounded bg-white/5 animate-pulse" />
        </div>
        {/* KPI skeletons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="rounded-2xl border border-white/10 bg-[hsl(var(--pane))] p-5 space-y-3" style={{ boxShadow: 'var(--shadow-elev-1)' }}>
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-white/5 animate-pulse" />
                <div className="w-14 h-5 rounded-full bg-white/5 animate-pulse" />
              </div>
              <div className="space-y-2">
                <div className="h-3 w-20 rounded bg-white/5 animate-pulse" />
                <div className="h-8 w-16 rounded bg-white/5 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
        {/* Chart + Links skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          <div className="lg:col-span-2 rounded-2xl border border-white/10 bg-[hsl(var(--pane-2))] p-6 h-[300px] animate-pulse" />
          <div className="rounded-2xl border border-white/10 bg-[hsl(var(--pane))] p-5 space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="flex justify-between">
                  <div className="h-3 w-32 rounded bg-white/5 animate-pulse" />
                  <div className="h-3 w-8 rounded bg-white/5 animate-pulse" />
                </div>
                <div className="h-1.5 rounded-full bg-white/5 animate-pulse" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-full" data-testid="analytics-page">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* ═══════════════════════════════════════════════════════ */}
        {/* HERO: Real-time Intelligence                          */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div className="flex items-start justify-between gap-6 mb-8 animate-fade-in-up" data-testid="analytics-hero">
          <div>
            <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-warm))] font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Command Center
            </p>
            <h1
              className="text-4xl sm:text-5xl font-semibold tracking-tight text-foreground mb-2"
              style={{ fontFamily: 'Space Grotesk, sans-serif' }}
              data-testid="analytics-heading"
            >
              Real-time Intelligence
            </h1>
            <p className="text-base text-muted-foreground max-w-lg">
              Monitor campaign performance, track engagement metrics, and analyze delivery health in real-time.
            </p>
          </div>

          {/* System Status badge */}
          <div className="flex-shrink-0 rounded-xl border border-[hsl(var(--status-success)/0.25)] bg-[hsl(var(--status-success)/0.08)] px-4 py-2.5 flex items-center gap-2" data-testid="system-status-badge">
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[hsl(var(--status-success))] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[hsl(var(--status-success))]" />
            </span>
            <span className="text-xs font-semibold text-[hsl(var(--status-success))]">System Status: Online</span>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* KPI CARDS                                             */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8 stagger-grid" data-testid="analytics-kpi-grid">
          <KpiCard icon={Send} label="Total Sent" value={totalSent.toLocaleString()} delta={totalSent > 0 ? summary?.sent_delta : null} deltaType="up" iconColor="hsl(var(--accent-warm))" />
          <KpiCard icon={Eye} label="Total Opens" value={totalOpens.toLocaleString()} delta={totalOpens > 0 ? summary?.opens_delta : null} deltaType="up" iconColor="hsl(var(--status-info))" />
          <KpiCard icon={MousePointerClick} label="Total Clicks" value={totalClicks.toLocaleString()} delta={totalClicks > 0 ? summary?.clicks_delta : null} deltaType="down" iconColor="hsl(var(--status-warning))" />
          <KpiCard icon={Activity} label="Open Rate" value={`${openRate}%`} delta={totalSent > 0 ? summary?.rate_delta : null} deltaType="up" iconColor="hsl(var(--status-success))" />
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* CAMPAIGN PERFORMANCE + TOP CLICKED LINKS              */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          {/* Bar Chart (2 cols) */}
          <div
            className="lg:col-span-2 rounded-2xl border border-white/10 bg-[hsl(var(--pane-2))] p-6 relative overflow-hidden"
            style={{ boxShadow: 'var(--shadow-elev-1)' }}
            data-testid="campaign-performance-chart"
          >
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(135deg, hsl(var(--accent-warm) / 0.05), transparent 50%)' }} />
            <div className="relative z-10">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-warm))] font-semibold mb-1">Campaign Performance</p>
                  <p className="text-sm text-muted-foreground">Engagement trends over time</p>
                </div>
                <Tabs value={chartRange} onValueChange={setChartRange} className="w-auto">
                  <TabsList className="h-8 bg-white/5 border border-white/10">
                    <TabsTrigger value="week" className="text-xs h-6 px-3 data-[state=active]:bg-[hsl(var(--accent-warm)/0.15)] data-[state=active]:text-[hsl(var(--accent-warm))]" data-testid="chart-toggle-week">Week</TabsTrigger>
                    <TabsTrigger value="month" className="text-xs h-6 px-3 data-[state=active]:bg-[hsl(var(--accent-warm)/0.15)] data-[state=active]:text-[hsl(var(--accent-warm))]" data-testid="chart-toggle-month">Month</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="h-[220px]" data-testid="campaign-performance-bars">
                {hasChartData ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} barSize={chartRange === 'week' ? 32 : 28}>
                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: 'hsl(var(--chart-label))', fontSize: 11 }} />
                    <YAxis axisLine={false} tickLine={false} tick={{ fill: 'hsl(var(--chart-label))', fontSize: 11 }} />
                    <RechartsTooltip content={<ChartTooltip />} cursor={false} />
                    <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                      {chartData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={index === chartData.length - 1
                            ? 'hsl(var(--chart-highlight))'
                            : 'hsl(var(--chart-bar))'}
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-center">
                    <BarChart3 className="w-10 h-10 mb-3 text-muted-foreground/15" />
                    <p className="text-sm text-muted-foreground/50">No campaign data yet</p>
                    <p className="text-[10px] text-muted-foreground/30 mt-1">Send campaigns to see performance trends</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Clicked Links (1 col) */}
          <div
            className="rounded-2xl border border-white/10 bg-[hsl(var(--pane))] p-5"
            style={{ boxShadow: 'var(--shadow-elev-1)' }}
            data-testid="top-clicked-links"
          >
            <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-warm))] font-semibold mb-4">Top Clicked Links</p>
            <div className="space-y-4">
              {topLinks.length === 0 ? (
                <div className="text-center py-8">
                  <Link2 className="w-8 h-8 mx-auto mb-2 text-muted-foreground/20" />
                  <p className="text-xs text-muted-foreground/50">No link click data yet</p>
                  <p className="text-[10px] text-muted-foreground/30 mt-1">Send campaigns to track engagement</p>
                </div>
              ) : topLinks.map((link, i) => (
                <div key={i} className="space-y-1.5" data-testid={`top-link-${i}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground flex items-center gap-1.5 truncate max-w-[70%]">
                      <Link2 className="w-3 h-3 flex-shrink-0" />
                      {link.url}
                    </span>
                    <span className="font-mono font-semibold text-foreground">{link.clicks}</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${link.pct}%`,
                        backgroundColor: i === 0 ? 'hsl(var(--accent-warm))' : 'hsl(var(--chart-bar-muted))',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            {topLinks.length > 0 && (
              <Button
                variant="outline"
                className="w-full mt-5 rounded-xl border-white/10 bg-white/5 hover:bg-white/[0.08] text-xs gap-2"
                onClick={() => handleExport('clicks')}
                data-testid="download-link-report"
              >
                <Download className="w-3.5 h-3.5" /> Download Full Report
              </Button>
            )}
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════ */}
        {/* RECENT EVENTS TABLE                                   */}
        {/* ═══════════════════════════════════════════════════════ */}
        <div
          className="rounded-2xl border border-white/10 bg-[hsl(var(--pane))] overflow-hidden animate-fade-in-up"
          style={{ boxShadow: 'var(--shadow-elev-1)', animationDelay: '0.3s' }}
          data-testid="recent-events-table"
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
            <div>
              <p className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--accent-warm))] font-semibold mb-0.5">Recent Events</p>
              <p className="text-xs text-muted-foreground">Security operations log</p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="h-8 text-xs rounded-lg border-white/10 bg-white/5 gap-1.5" onClick={() => handleExport('events')} data-testid="export-events-btn">
                <Download className="w-3 h-3" /> Export
              </Button>
            </div>
          </div>

          {/* Table header */}
          <div className="grid grid-cols-[200px_1fr_120px_120px] gap-4 px-6 py-3 border-b border-white/5">
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Event Source</span>
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Description</span>
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground">Status</span>
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-muted-foreground text-right">Timestamp</span>
          </div>

          {/* Table rows */}
          {recentEvents.length === 0 ? (
            <div className="text-center py-12">
              <Activity className="w-8 h-8 mx-auto mb-2 text-muted-foreground/15" />
              <p className="text-sm text-muted-foreground/50">No events recorded yet</p>
              <p className="text-[10px] text-muted-foreground/30 mt-1">Campaign sends, opens, and clicks will appear here</p>
            </div>
          ) : recentEvents.map((evt, i) => {
            const evtSource = evt.source || evt.event_type || 'System';
            const evtDesc = evt.desc || evt.description || evt.details || '';
            const evtStatus = evt.status || 'info';
            const evtTime = evt.time || evt.timestamp || '';
            const timeStr = typeof evtTime === 'string' && evtTime.includes('ago')
              ? evtTime
              : evtTime ? new Date(evtTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

            const EvtIcon = evtStatus === 'warning' ? AlertTriangle :
                            evtStatus === 'active' ? Radio :
                            evtStatus === 'secure' ? Shield : Activity;

            return (
              <div
                key={i}
                className="grid grid-cols-[200px_1fr_120px_120px] gap-4 items-center px-6 py-3.5 border-b border-white/5 last:border-b-0 hover:bg-white/[0.03] transition-colors duration-100"
                data-testid={`event-row-${i}`}
              >
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                    <EvtIcon className="w-3.5 h-3.5 text-muted-foreground" />
                  </div>
                  <span className="text-sm font-medium text-foreground truncate">{evtSource}</span>
                </div>
                <span className="text-sm text-muted-foreground truncate">{evtDesc}</span>
                <EventStatusBadge status={evtStatus} />
                <span className="text-xs font-mono text-muted-foreground text-right">{timeStr}</span>
              </div>
            );
          })}

          {/* Footer CTA */}
          <div className="flex items-center justify-center px-6 py-4 border-t border-white/10">
            <Button variant="link" className="text-[hsl(var(--accent-warm))] text-sm gap-1.5 p-0 h-auto" data-testid="view-full-log-btn">
              View Comprehensive Log History <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

      </div>
    </ScrollArea>
  );
}
